import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowUpRight, Clock, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import SEO, { orgSchema, breadcrumbSchema } from "@/components/SEO";
import { PageHero, SectionHeading } from "@/components/sections";
import { Reveal, stagger, item } from "@/components/motion";
import { ARTICLES, CATEGORIES } from "@/data/blog";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";

export default function Resources() {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("All");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const crumbs = [{ name: "Home", path: "/" }, { name: "Resources", path: "/resources" }];

  const filtered = useMemo(() => {
    return ARTICLES.filter((a) => {
      const matchCat = cat === "All" || a.category === cat;
      const q = query.toLowerCase();
      const matchQ = !q || a.title.toLowerCase().includes(q) || a.excerpt.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [query, cat]);

  const featured = ARTICLES[0];
  const rest = filtered.filter((a) => !(cat === "All" && !query && a.slug === featured.slug));

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try { await subscribeNewsletter(email); toast.success("You're subscribed. Welcome to Beacon."); setEmail(""); }
    catch { toast.error("Please enter a valid email."); }
    finally { setLoading(false); }
  };

  return (
    <>
      <SEO title="Resources & Insights" path="/resources"
        description="Evidence-based mental health articles from Beacon Counseling — practical CBT skills, guides to anxiety, depression, panic, and parenting, written by a licensed Connecticut CBT counselor."
        jsonLd={[orgSchema, breadcrumbSchema(crumbs)]} />
      <PageHero eyebrow="Resources" breadcrumb={crumbs}
        title="Practical insights for a clearer mind."
        intro="Evidence-based tools, guides, and stories from our founder, David Pecirep, LPC — written to help you understand your mind and take real, practical steps forward."
        image="https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" />

      {/* Featured */}
      {cat === "All" && !query && (
        <section className="py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <Reveal>
              <Link to={`/resources/${featured.slug}`} className="group grid overflow-hidden rounded-[2rem] border border-navy-100 bg-white shadow-soft lg:grid-cols-2" data-testid="featured-article">
                <div className="relative overflow-hidden">
                  <img src={featured.image} alt={featured.title} className="h-full w-full object-cover aspect-[16/10] transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute left-5 top-5 rounded-full bg-teal px-3 py-1 text-xs font-medium text-white">Featured · {featured.category}</span>
                </div>
                <div className="flex flex-col justify-center p-8 lg:p-12">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground"><Clock className="h-4 w-4" />{featured.readTime}</div>
                  <h2 className="mt-4 font-display text-3xl text-navy lg:text-4xl group-hover:text-teal transition-colors">{featured.title}</h2>
                  <p className="mt-4 text-muted-foreground leading-relaxed">{featured.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-2 font-medium text-teal">Read article <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
                </div>
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* Search + filters */}
      <section className="pb-8">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search articles…"
                className="h-12 rounded-full pl-12" data-testid="blog-search" />
            </div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c}`}
                  className={`rounded-full px-4 py-2 text-sm transition-colors ${cat === c ? "bg-navy text-white" : "bg-secondary text-navy/70 hover:bg-softblue/60"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-24 lg:pb-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          {rest.length === 0 ? (
            <p className="py-16 text-center text-muted-foreground">No articles found. Try a different search.</p>
          ) : (
            <motion.div key={cat + query} variants={stagger} initial="hidden" animate="show"
              className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {rest.map((a) => (
                <motion.article key={a.slug} variants={item}>
                  <Link to={`/resources/${a.slug}`} className="group flex h-full flex-col overflow-hidden rounded-3xl border border-navy-100 bg-white shadow-soft transition-all hover:-translate-y-1 hover:shadow-premium" data-testid="article-card">
                    <div className="relative overflow-hidden">
                      <img src={a.image} alt={a.title} className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-navy backdrop-blur">{a.category}</span>
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" />{a.readTime}</div>
                      <h3 className="mt-3 font-display text-xl text-navy group-hover:text-teal transition-colors">{a.title}</h3>
                      <p className="mt-2 flex-1 text-sm text-muted-foreground leading-relaxed line-clamp-3">{a.excerpt}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-teal">Read more <ArrowUpRight className="h-3.5 w-3.5" /></span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="pb-24 lg:pb-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-navy px-8 py-16 text-center sm:px-16 shadow-premium">
            <div className="pointer-events-none absolute -top-20 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-teal/20 blur-3xl" />
            <div className="relative mx-auto max-w-2xl">
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-white">Get practical tools in your inbox.</h2>
              <p className="mt-4 text-white/65">Join our newsletter for evidence-based mental health insights. No spam, ever.</p>
              <form onSubmit={subscribe} className="mx-auto mt-8 flex max-w-md gap-3" data-testid="resources-newsletter">
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com"
                  className="h-13 py-6 rounded-full bg-white/10 border-white/15 text-white placeholder:text-white/40" data-testid="resources-newsletter-input" />
                <Button type="submit" disabled={loading} className="h-13 py-6 rounded-full bg-teal hover:bg-teal-dark px-7 shrink-0" data-testid="resources-newsletter-submit">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Subscribe"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
